import IRootBuilderModel from "../../interfaces/builderModelInterfaces";
import store from "../../state/store";
import { IOrganizationDocument } from "../../interfaces/organizationDocumentInterfaces";
import Organization from "../organization";
import { IPeekerOptions } from "../../services/peeker";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import Folder from "../../../formability/document_management/models/folder";
import PublicProfileProvider from "../../providers/publicProfileProvider";
import PublicProfileBuilderModel from "../tracked/publicProfileBuilderModel";
import Validatable from "../../mixins/validatable";
import PublicProfileValidation from "../navigableValidations/publicProfileValidation";
import { AttributeMetadataInterface } from "../../interfaces/attributeMetadataInterface";
import { ApplicationType } from "../../../formability/licenseBuilder/enums/applicationType";
import { LicenseType } from "../../../formability/licenseBuilder/enums/licenseTypes";
import { DocumentOwnerType } from "../../enums/documentOwnerType";
import LicenseResource from "../../../formability/licenseBuilder/models/licensing/licenseResource";
import { RelationType } from "../relationType";
import LicenseStep from "../../../formability/licenseBuilder/models/licensing/licenseStep";
import RegistrationStep from "../../../formability/licenseBuilder/models/licensing/registrationStep";
import MaintenanceStep from "../../../formability/licenseBuilder/models/licensing/maintenanceStep";
import LicenseStepFactory from "../../../formability/licenseBuilder/models/licensing/licenseStepFactory";
import { ObjectHelper } from "../../helpers/objectHelper";
import Form from "../../../formability/form_builder/models/form";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import Share from "~/models/share";
import Shareable from "~/mixins/shareable";
import {DocumentType} from "~/enums/documentType";
import Capable from '~/mixins/capable';

export default class PublicProfile
  extends Validatable(Shareable(Capable(PublicProfileBuilderModel)))
  implements IRootBuilderModel, IOrganizationDocument {
  // base fields
  _classKey = "publicProfile";
  collectionKey = 'public_profiles';
  publicProfile = this;
  _organization: Organization;
  _application: Form;
  attributes_metadata: { [key: string]: AttributeMetadataInterface };
  // organization document fields
  author: any;
  folder_id: string;
  title: string;
  u_at: string;
  c_at: string;
  is_published: boolean;

  // public profile fields
  application_type: ApplicationType;
  application_jurisdiction: LicenseType;
  application_owner_type: DocumentOwnerType;
  apply_custom_text_enabled: boolean;
  apply_section_header: string;
  apply_sub_text: string;
  apply_button_label: string;
  seo_keywords: string[];
  resources: { [key: string]: LicenseResource } = {};
  get resourcesArray(): LicenseResource[] {
    return ObjectHelper.toArray(this.resources).sort(
      (a, b) => a.sort_order - b.sort_order
    );
  }
  registration_steps: { [key: string]: RegistrationStep } = {};
  get registrationStepsArray(): RegistrationStep[] {
    return ObjectHelper.toArray(this.registration_steps).sort(
      (a, b) => a.sort_order - b.sort_order
    );
  }
  maintenance_steps: { [key: string]: MaintenanceStep } = {};
  get maintenanceStepsArray(): MaintenanceStep[] {
    return ObjectHelper.toArray(this.maintenance_steps).sort(
      (a, b) => a.sort_order - b.sort_order
    );
  }
  // ******************** EMBEDDED MODELS ******************** //

  get EMBEDDED_MODELS(): {
    [key: string]: { type: any; relationType: RelationType };
  } {
    return {
      registration_steps: {
        type: LicenseStep,
        relationType: RelationType.dictionary
      },
      maintenance_steps: {
        type: LicenseStep,
        relationType: RelationType.dictionary
      },
      resources: {
        type: LicenseResource,
        relationType: RelationType.dictionary
      },
      shares: {
        type: Share,
        relationType: RelationType.collection
      },
    };
  }

  createEmbeddedObject = {
    registration_steps: (parent: PublicProfile, json): LicenseStep => {
      return new RegistrationStep(parent);
    },
    maintenance_steps: (parent: PublicProfile, json): LicenseStep => {
      return new MaintenanceStep(parent);
    },
    resources: (parent: PublicProfile, json): LicenseResource => {
      return new LicenseResource(parent);
    }
  };

  createResource(): LicenseResource {
    const resource = new LicenseResource(this);
    this.placeInDictionary(resource);
    this.trackCreationDeletion(
      resource.client_id,
      null,
      resource.asJson(),
      resource
    );
    this.saveChangesToStore();
    return resource;
  }

  createStep(stepType): LicenseStep {
    const step = LicenseStepFactory.create(this, stepType);
    this.placeInDictionary(step);
    this.trackCreationDeletion(step.client_id, null, step.asJson(), step);
    this.saveChangesToStore();
    return step;
  }

  // ******************** EMBEDDED END ******************** //

  // ******************** CLASS CONFIGURATIONS ******************** //
  set organization(val: Organization) {
    this.asJson();
    this._organization = val;
  }

  get organization(): Organization {
    if (this._organization) {
      // for TrackedModel implementation
      return this._organization;
    } else if (this.parent instanceof Organization) {
      // for OrganizationDocument implementation
      return this.parent;
    } else {
      return this.parent.organization;
    }
  }

  set application(val: Form) {
    this.asJson();
    this._application = val;
  }

  get application(): Form {
    if (this._application) {
      // for TrackedModel implementation
      return this._application;
    }
  }

  get classKey(): string {
    return this._classKey;
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Public Profile";
    }
  }

  static get displayName() {
    return "Public Profile";
  }

  get docType(): DocumentType {
    return DocumentType.publicProfile;
  }

  get rootName() {
    return "publicProfile";
  }

  static repo: IRepo<PublicProfile, PublicProfileProvider> = new Repo<
    PublicProfile,
    PublicProfileProvider
  >(
    "public_profiles",
    PublicProfile,
    {},
    new PublicProfileProvider(
      PublicProfile,
      "public_profile",
      "public_profiles"
    )
  );
  repo: IRepo<this, PublicProfileProvider<this>>;

  static get provider(): PublicProfileProvider {
    return this.repo.provider;
  }

  get provider(): this["repo"]["provider"] {
    return this.repo.provider;
  }

  canUnpublish(): boolean {
    return this.is_published;
  }

  //******************** VALIDATABLE ********************//
  _validation: PublicProfileValidation = new PublicProfileValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case "forms":
        return value.title;
      case "status_change_period_start":
        return "Period Start";
      case "status_change_period_end":
        return "Period End";
      case "generated_at":
        return "Generated At";
      case "generation_period_end":
        return "Generation Period End";
      default:
        return this.attributes_metadata[propName].display_name;
    }
  }

  //******************** VALIDATABLE END ********************//

  // ******************** TRACK CHANGES ******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(
        this.lastPeekerOptions || {
          area: this.currentBuilderArea(),
          modelId: this.clientIdOrId
        }
      );
      if (this.attempted_to_publish) {
        this.validate();
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.storeName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(
    changes: any,
    peekerOptions: string | IPeekerOptions = {
      area: this.currentBuilderArea(),
      modelId: this.clientIdOrId
    }
  ): void {
    store.commit(`${this.storeName}/saveChanges`, { changes, peekerOptions });
  }

  except = [
    "registration_steps",
    "maintenance_steps",
    "resources",
    "publicProfile",
    "organization",
    "saveTimer",
    "_organization",
    "attributes_metadata",
    "application",
    "shares",
  ];

  get UNTRACKED_PROPERTIES(): string[] {
    return ["u_at"];
  }

  get sortedResources(): LicenseResource[] {
    return this.sortProperty("resources");
  }

  get sortedRegistrationSteps(): RegistrationStep[] {
    return this.sortProperty("registration_steps");
  }

  get sortedMaintenanceSteps(): MaintenanceStep[] {
    return this.sortProperty("maintenance_steps");
  }

  // ******************** TRACK CHANGES END ******************** //

  get isPublished(): boolean {
    return this.is_published && Date.now() > Date.parse(this.u_at);
  }

  get isArchive(): boolean {
    return (
      this.parent instanceof Folder &&
      (this.parent.name === "Archive" || this.parent.name === "Trash")
    );
  }

  canMoveTo(folder: Folder): boolean {
    return !folder.isArchiveFolder();
  }

  // ******************** CLASS CONFIGURATIONS END ******************** //

  reg_step_icon(item) {
    switch (item) {
      case "inspection":
        return "inspection";
      case "document":
        return "form-stack";
      case "supplementalLicense":
        return "attention";
      case "education":
        return "education";
      case "urgent":
        return "attention";
      case "exam":
        return "exam";
      case "backgroundCheck":
        return "bk-check";
      case "payment":
        return "dollar";
      case "experience":
        return "experience";
      case "other":
        return "other";
      default:
        return "";
    }
  }
  reg_step_color(item) {
    switch (item) {
      case "inspection":
        return "green";
      case "document":
        return "blue";
      case "supplementalLicense":
        return "blush";
      case "education":
        return "dark-blue";
      case "urgent":
        return "blush";
      case "exam":
        return "brown";
      case "backgroundCheck":
        return "dark-purple";
      case "payment":
        return "brown";
      case "experience":
        return "orange";
      case "other":
        return "lake";
      default:
        return "grey";
    }
  }

  onestop_reg_step_icon_data(item) {
    switch (item) {
      case "inspection":
        return { text: 'Inspection', icon: 'inspection'};
      case "document":
        return { text: 'Documents', icon: 'file'};
      case "supplementalLicense":
        return { text: 'Supplemental License', icon: 'requirements'};
      case "education":
        return { text: 'Education', icon: 'education'};
      case "urgent":
        return "attention";
      case "exam":
        return { text: 'Exam', icon: 'exam'};
      case "backgroundCheck":
        return { text: 'Background Check', icon: 'bk-check'};
      case "payment":
        return { text: 'Payment', icon: 'payment'};
      case "experience":
        return { text: 'Experience', icon: 'experience'};
      case "other":
        return { text: 'Other', icon: 'other'};
      default:
        return { text: '', icon: '' };
    }
  }

  _moving: boolean = false
  get moving(): boolean {
    return this._moving
  }

  set moving(val: boolean) {
    this._moving = val
  }
}
